import { computed, type ComputedRef, type Ref } from 'vue';
import { camelCase, first, isNil } from 'lodash';

import {
  SpotStatus,
  SpotEventTypeEnum,
  YardTaskStatus,
  DatabaseEntities,
  type ISpot,
  type Maybe,
  type Nullable
} from '@nova/core';
import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenterV2';
import type { SpotEventData } from '@/composables/useEventBasedDialog';
import { useSpotStatusStrategy } from '@/composables/useSpotStatusStrategy';
import { useYardTaskByRelatedEntityPresenterV2 } from '@satellite/composables/useYardTaskByRelatedEntityPresenterV2';
import { useYardSpotStatus } from '@satellite/composables/useYardSpotStatus';
import {
  useYardSpotPresenterV2,
  type YardSpotDetails,
  type YardSpotAssignmentDetails,
  type YardSpotReserveDetails
} from '@satellite/composables/useYardSpotPresenterV2';
import { useYardAreaPresenterV2 } from '@satellite/composables/useYardAreaPresenterV2';
import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterV2Placeholder';

export interface ReserveInfo {
  entityName: string;
  objectId: string;
}

export interface OccupiedByDetails {
  carrierCompany: Nullable<string>;
  visitType: Nullable<string>;
}

export interface UseCheckSpotPanelOptions {
  spotId: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;
  spot?:
    | Ref<Maybe<PresenterPlaceholderData<YardSpotDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardSpotDetails>>>;
  spotEvents?: Ref<Maybe<SpotEventData[]>> | ComputedRef<Maybe<SpotEventData[]>>;
}

/** Minimal asset representation returned to consumers of this composable. */
interface AssetInfo {
  id: string;
  code: Nullable<string>;
  type: Nullable<string>;
}

export type OccupiedView = 'tabs' | 'carrierVisit' | 'carrierVisitHooked' | 'unhooked' | 'fallback';

function assetContainerWithId(container: Maybe<YardAssetDetails>): Nullable<YardAssetDetails> {
  return container?.id ? container : null;
}

interface LoadTypeJoin {
  name?: string;
  direction?: string;
}

interface CompanyJoin {
  name?: string;
}

interface UserWithCompany {
  company?: CompanyJoin | null;
}

interface AppointmentJoin {
  user?: UserWithCompany | null;
  loadType?: LoadTypeJoin | null;
}

function resolveLoadType(loadType: Maybe<LoadTypeJoin>): Nullable<string> {
  if (!loadType) {
    return null;
  }

  return [loadType.name, loadType.direction].filter(Boolean).join(' • ') || null;
}

function assetFromContainerJoin(
  container: Maybe<{ id?: Maybe<string>; code?: Maybe<string>; type?: Maybe<string> }>
): Nullable<AssetInfo> {
  if (!container?.id) {
    return null;
  }

  return {
    id: container.id,
    code: container.code ?? null,
    type: container.type ?? null
  };
}

function occupiedByFromAppointment(appointment: Maybe<AppointmentJoin>): OccupiedByDetails {
  return {
    carrierCompany: appointment?.user?.company?.name ?? null,
    visitType: resolveLoadType(appointment?.loadType)
  };
}

export function useCheckSpotPanel({ spotId, spot, spotEvents }: UseCheckSpotPanelOptions) {
  const { data: relatedMoveTasks } = useYardTaskByRelatedEntityPresenterV2({
    relatedEntity: DatabaseEntities.Spot,
    relatedIds: computed(() => [spotId.value ?? spot?.value?.id ?? '']),
    statuses: [YardTaskStatus.CREATED, YardTaskStatus.IN_PROGRESS],
    type: 'move',
    mode: EntityPresenterV2Mode.Live,
    headless: true
  });

  const yardSpotStatus = useYardSpotStatus({
    getActiveMoveTasksForSpot: () => relatedMoveTasks.value
  });

  const {
    data: currentSpot,
    isLoading,
    onIntersect: onIntersectSpot
  } = useYardSpotPresenterV2({
    spotId,
    placeholderSpotData: spot,
    headless: true
  });

  const spotCode = computed((): Nullable<string> => currentSpot.value?.code ?? null);
  const areaId = computed((): Maybe<string> => currentSpot.value?.spotAreaId ?? null);
  const spotDetail = computed((): Nullable<YardSpotDetails> => currentSpot.value ?? null);
  const spotType = computed((): Nullable<string> => currentSpot.value?.type ?? null);

  const spotStatus = computed((): SpotStatus => {
    if (!spotDetail.value) {
      return SpotStatus.UNKNOWN;
    }

    return yardSpotStatus.getRawStatus(spotDetail.value as ISpot);
  });

  const isOccupied = computed(
    (): boolean =>
      Boolean(spotDetail.value) && yardSpotStatus.isSpotAssigned(spotDetail.value as ISpot)
  );
  const isReserved = computed(
    (): boolean =>
      Boolean(spotDetail.value) && yardSpotStatus.isSpotReserved(spotDetail.value as ISpot)
  );
  const isOpenStatus = computed(
    (): boolean => Boolean(spotDetail.value) && yardSpotStatus.isSpotOpen(spotDetail.value as ISpot)
  );
  const isClosedStatus = computed(
    (): boolean =>
      Boolean(spotDetail.value) && yardSpotStatus.isSpotClosed(spotDetail.value as ISpot)
  );

  const activeSpotAssignment = computed((): Nullable<YardSpotAssignmentDetails> => {
    const spotAssignments = spotDetail.value?.spotAssignments;
    if (!spotAssignments || spotAssignments.length === 0) {
      return null;
    }

    const activeAssignment = spotAssignments.find(
      (sa: YardSpotAssignmentDetails): boolean => sa.isActive
    );
    if (activeAssignment) {
      return activeAssignment;
    }

    return first(spotAssignments) ?? null;
  });

  const currentAsset = computed((): Nullable<AssetInfo> => {
    if (!activeSpotAssignment.value) {
      return null;
    }

    return (
      assetFromContainerJoin(activeSpotAssignment.value.assetContainer) ??
      assetFromContainerJoin(first(activeSpotAssignment.value.assetVisit?.assetContainers)) ??
      null
    );
  });

  const hasCarrierVisit = computed((): boolean => Boolean(activeSpotAssignment.value?.assetVisit));

  const hookedAsset = computed(
    (): Nullable<YardAssetDetails> =>
      assetContainerWithId(first(activeSpotAssignment.value?.assetVisit?.assetContainers))
  );

  const unhookedAsset = computed(
    (): Nullable<YardAssetDetails> =>
      assetContainerWithId(activeSpotAssignment.value?.assetContainer)
  );

  const activeAssetVisit = computed(() => activeSpotAssignment.value?.assetVisit ?? null);

  const occupiedView = computed<OccupiedView>(() => {
    if (!isOccupied.value) {
      return 'fallback';
    }
    const hooked = Boolean(hookedAsset.value);
    const unhooked = Boolean(unhookedAsset.value);
    if (hasCarrierVisit.value) {
      if (unhooked) {
        return 'tabs';
      }
      return hooked ? 'carrierVisitHooked' : 'carrierVisit';
    }
    return unhooked ? 'unhooked' : 'fallback';
  });

  const activeSpotReserve = computed(
    (): Nullable<YardSpotReserveDetails> =>
      spotDetail.value?.spotReserves?.find((r: YardSpotReserveDetails): boolean => r.isActive) ??
      null
  );

  const reserveInfo = computed((): Nullable<ReserveInfo> => {
    const reserve = activeSpotReserve.value;
    if (!reserve?.objectId || !reserve.entityName) {
      return null;
    }

    return {
      entityName: reserve.entityName,
      objectId: reserve.objectId
    };
  });

  const { data: currentArea, onIntersect: onIntersectArea } = useYardAreaPresenterV2({
    areaId,
    headless: true
  });

  const areaName = computed((): Nullable<string> => currentArea.value?.name ?? null);

  function latestEventOfType(eventType: SpotEventTypeEnum): Nullable<SpotEventData> {
    const events = spotEvents?.value;
    if (!Array.isArray(events)) {
      return null;
    }
    const matching = events
      .filter(e => e.eventType === eventType)
      .sort(
        (a, b) =>
          new Date(b.createDateTime ?? 0).getTime() - new Date(a.createDateTime ?? 0).getTime()
      );
    return matching[0] ?? null;
  }

  const latestCloseEvent = computed(
    (): Nullable<SpotEventData> => latestEventOfType(SpotEventTypeEnum.CLOSE)
  );

  const latestOpenEvent = computed(
    (): Nullable<SpotEventData> => latestEventOfType(SpotEventTypeEnum.OPEN)
  );

  const strategy = useSpotStatusStrategy({
    spotDetail,
    activeSpotAssignment,
    activeSpotReserve,
    latestCloseEvent,
    latestOpenEvent,
    spotStatus
  });

  const statusColor = computed(() => strategy.value.color.value);
  const statusIcon = computed(() => strategy.value.icon.value);
  const statusIconColor = computed(() => {
    const color = strategy.value.color.value;
    if (color === 'default') {
      return;
    }
    return color;
  });
  const isStatusChipTonal = computed(() => strategy.value.isTonal.value);
  const durationCaptionKey = computed(() => strategy.value.durationCaptionKey.value);
  const statusReferenceDateIso = computed(
    (): Nullable<string> => strategy.value.referenceDate.value?.toISOString() ?? null
  );

  const openMoveTaskRef = computed((): Nullable<string> => {
    const task = first(relatedMoveTasks.value);
    if (isNil(task?.refNum)) {
      return null;
    }
    return String(task?.refNum);
  });

  const occupiedBy = computed((): Nullable<OccupiedByDetails> => {
    if (!isOccupied.value) {
      return null;
    }

    if (!activeSpotAssignment.value) {
      return { carrierCompany: null, visitType: null };
    }

    const { assetVisit, assetContainer } = activeSpotAssignment.value;
    if (assetVisit?.appointment) {
      const occupiedByDetails = occupiedByFromAppointment(
        assetVisit.appointment as AppointmentJoin
      );
      if (occupiedByDetails.carrierCompany || occupiedByDetails.visitType) {
        return occupiedByDetails;
      }
    }

    if (assetContainer) {
      const appointment = (assetContainer.dropAppointment ?? assetContainer.pickupAppointment) as
        | AppointmentJoin
        | undefined;
      const occupiedByDetails = occupiedByFromAppointment(appointment);
      if (occupiedByDetails.carrierCompany || occupiedByDetails.visitType) {
        return occupiedByDetails;
      }
    }

    const firstAssetContainerFromVisit = first(assetVisit?.assetContainers);
    if (firstAssetContainerFromVisit) {
      const appointment = (firstAssetContainerFromVisit.dropAppointment ??
        firstAssetContainerFromVisit.pickupAppointment) as AppointmentJoin | undefined;
      return occupiedByFromAppointment(appointment);
    }

    return { carrierCompany: null, visitType: null };
  });

  const reservedTaskLabel = computed(
    (): Nullable<string> => openMoveTaskRef.value || reserveInfo.value?.objectId || null
  );

  const showOccupiedAssetFallback = computed((): boolean => {
    if (!currentAsset.value?.id) {
      return false;
    }
    const occupiedByDetails = occupiedBy.value;
    if (!occupiedByDetails) {
      return false;
    }
    return !occupiedByDetails.carrierCompany && !occupiedByDetails.visitType;
  });

  const closeReason = computed((): Nullable<string> => {
    if (!latestCloseEvent.value?.reason) {
      return null;
    }
    return camelCase(latestCloseEvent.value.reason);
  });

  const closeObservation = computed(
    (): Nullable<string> => latestCloseEvent.value?.observation ?? null
  );

  function onIntersect(entry?: { isIntersecting?: boolean } | boolean): void {
    onIntersectSpot(entry);
    onIntersectArea(entry);
  }

  return {
    spotCode,
    spotType,
    spotStatus,
    areaId,
    areaName,
    currentAsset,
    hookedAsset,
    unhookedAsset,
    activeAssetVisit,
    hasCarrierVisit,
    occupiedView,
    reserveInfo,
    openMoveTaskRef,
    occupiedBy,
    isLoading,
    isOccupied,
    isReserved,
    isOpenStatus,
    isClosedStatus,
    statusColor,
    statusIcon,
    statusIconColor,
    durationCaptionKey,
    statusReferenceDateIso,
    reservedTaskLabel,
    isStatusChipTonal,
    showOccupiedAssetFallback,
    closeReason,
    closeObservation,
    onIntersect
  };
}
